// openEHR RM model — BMM package org.openehr.rm.composition.content.entry
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { LOCATABLE, PATHABLE } from "../../common/archetyped.ts";
import { PARTY_SELF } from "../../common/generic.ts";
import type { PARTY_PROXY } from "../../common/generic.ts";
import type { ITEM_STRUCTURE } from "../../data_structures/item_structure.ts";
import type { DV_PARSABLE } from "../../data_types/encapsulated.ts";
import type { DV_DATE_TIME } from "../../data_types/quantity/date_time.ts";
import type { CODE_PHRASE, DV_CODED_TEXT, DV_TEXT } from "../../data_types/text.ts";
import { CONTENT_ITEM } from "../content.ts";
import * as openehr_base from "../../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../../base/apply_rm_init.ts";

/**
 * The abstract parent of all \`ENTRY\` subtypes. An \`ENTRY\` is the root of a logical item of  hard  clinical information created in the  clinical statement  context, within a clinical session. There can be numerous such contexts in a clinical session. Observations and other Entry types only ever document information captured/created in the event documented by the enclosing Composition.
 *
 * An \`ENTRY\` is also the minimal unit of information any query should return, since a whole \`ENTRY\` (including subparts) records spatial structure, timing information, and contextual information, as well as the subject and generator of the information.
 */
export abstract class ENTRY extends CONTENT_ITEM {
  /**
   * Mandatory indicator of the localised language in which this Entry is written. Coded from openEHR Code Set  languages .
   */
  language?: CODE_PHRASE;
  /**
   * Name of character set in which text values in this Entry are encoded. Coded from openEHR Code Set  character sets.
   */
  encoding?: CODE_PHRASE;
  /**
   * Other participations at \`ENTRY\` level.
   */
  other_participations?: undefined;
  /**
   * Identifier of externally held workflow engine data for this workflow execution, for this subject of care.
   */
  workflow_id?: openehr_base.OBJECT_REF;
  /**
   * Id of human subject of this \`ENTRY\`, e.g.:
   *
   * * organ donor
   * * foetus
   * * a family member
   * * another clinically relevant person.
   */
  subject?: PARTY_PROXY;
  /**
   * Optional identification of provider of the information in this \`ENTRY\`, which might be:
   *
   * * the patient
   * * a patient agent, e.g. parent, guardian
   * * the clinician
   * * a device or software
   *
   * Generally only used when the recorder needs to make it explicit. Otherwise, Composition composer and other participants are assumed.
   */
  provider?: PARTY_PROXY;
  /**
   * Returns True if this Entry is about the subject of the EHR, in which case the subject attribute is of type PARTY_SELF.
   * @returns Result value
   */
  subject_is_self(): openehr_base.Boolean {
    // Check if subject is an instance of PARTY_SELF
    return openehr_base.Boolean.from(this.subject instanceof PARTY_SELF);
  }
}

/**
 * Entry subtype for administrative information, i.e. information about setting up the clinical process, but not itself clinically relevant. Archetypes will define contained information.
 *
 * Used for administrative details of admission, episode, ward location, discharge, appointment (if not stored in a practice management or appointments system).
 *
 * Not to be used for any clinically significant information.
 */
export class ADMIN_ENTRY extends ENTRY {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Content of the Admin Entry.
   */
  data?: ITEM_STRUCTURE;
}

/**
 * The abstract parent of all clinical \`ENTRY\` subtypes. A \`CARE_ENTRY\` defines protocol and guideline attributes for all clinical Entry subtypes.
 */
export abstract class CARE_ENTRY extends ENTRY {
  /**
   * Description of the method (i.e. how) the information in this entry was arrived at. For \`OBSERVATIONs\`, this is a description of the method or instrument used. For \`EVALUATIONs\`, how the evaluation was arrived at. For \`INSTRUCTIONs\`, how to execute the Instruction. This may take the form of references to guidelines, including manually followed and executable; knowledge references such as a paper in Medline; clinical reasons within a larger care process.
   */
  protocol?: ITEM_STRUCTURE;
  /**
   * Optional external identifier of guideline creating this Entry if relevant.
   */
  guideline_id?: openehr_base.OBJECT_REF;
}

/**
 * Entry subtype for all clinical data in the past or present, i.e. which (by the time it is recorded) has already occurred. \`OBSERVATION\` data is expressed using the class \`HISTORY<T>\`, which guarantees that it is situated in time. \`OBSERVATION\` is used for all notionally objective (i.e. measured in some way) observations of phenomena, and patient-reported phenomena, e.g. pain.
 *
 * Not to be used for recording opinion or future statements of any kind, including instructions, intentions, plans etc.
 */
export class OBSERVATION extends CARE_ENTRY {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * The data of this observation, in the form of a history of values which may be of any complexity.
   */
  data?: undefined;
  /**
   * Optional recording of the state of subject of this observation during the observation process, in the form of a separate history of values which may be of any complexity. State may also be recorded within the History of the data attribute.
   */
  state?: undefined;
}

/**
 * Entry type for evaluation statements. Used for all kinds of statements which evaluate other information, such as interpretations of observations, diagnoses, differential diagnoses, hypotheses, risk assessments, goals and plans.
 *
 * Should not be used for actionable statements such as medication orders - these are represented using the \`INSTRUCTION\` type.
 */
export class EVALUATION extends CARE_ENTRY {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * The data of this evaluation, in the form of a spatial data structure.
   */
  data?: ITEM_STRUCTURE;
}

/**
 * Used to record a clinical action that has been performed, which may have been ad hoc, or due to the execution of an Activity in an Instruction workflow. Every Action corresponds to a careflow step of some kind or another.
 */
export class ACTION extends CARE_ENTRY {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Point in time at which this action completed. To indicate an unknown time, use a \`DV_DATE_TIME\` instance with \`_value_\` set to the time of creation (or some other known time before which the Action is known to have occurred, e.g. data accession timestamp from integration engine), and \`_magnitude_status_\` set to \`<\`.
   */
  time?: DV_DATE_TIME;
  /**
   * Details of transition in the Instruction state machine caused by this Action.
   */
  ism_transition?: ISM_TRANSITION;
  /**
   * Details of the Instruction that caused this Action to be performed, if there was one.
   */
  instruction_details?: INSTRUCTION_DETAILS;
  /**
   * Description of the action that has been performed, in the form of an archetyped structure.
   */
  description?: ITEM_STRUCTURE;
}

/**
 * Defines a single activity within an Instruction, such as a medication administration.
 */
export class ACTIVITY extends LOCATABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Timing of the activity, in the form of a parsable string. If used, the preferred syntax is ISO8601 'R' format, but other formats may be used including HL7 GTS.
   *
   * May be omitted if:
   *
   * * timing is represented structurally in the \`_description_\` attribute (e.g. via archetyped elements), or
   * * unavailable, e.g. imported legacy data; in such cases, the \`INSTRUCTION._narrative_\` should carry text that indicates the timing of its \`_activities_\`.
   */
  timing?: DV_PARSABLE;
  /**
   * Internal storage for action_archetype_id
   * @protected
   */
  protected _action_archetype_id?: openehr_base.String;

  /**
   * Perl-compliant regular expression pattern, enclosed in  '//' delimiters, indicating the valid identifiers of archetypes for Actions corresponding to this Activity specification.
   *
   * Defaults to  \`/.*\/\`, meaning any archetype.
   */
  get action_archetype_id(): string | undefined {
    return this._action_archetype_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for action_archetype_id.
   * Use this to access openehr_base.String methods.
   */
  get $action_archetype_id(): openehr_base.String | undefined {
    return this._action_archetype_id;
  }

  /**
   * Sets action_archetype_id from either a primitive value or openehr_base.String wrapper.
   */
  set action_archetype_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._action_archetype_id = undefined;
    } else if (typeof val === "string") {
      this._action_archetype_id = openehr_base.String.from(val);
    } else {
      this._action_archetype_id = val;
    }
  }

  /**
   * Description of the activity, in the form of an archetyped structure.
   */
  description?: ITEM_STRUCTURE;
}

/**
 * Model of a transition in the Instruction State Machine, caused by a careflow step. The attributes document the careflow step as well as the ISM transition.
 */
export class ISM_TRANSITION extends PATHABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * The ISM current state. Coded by openEHR terminology group Instruction states.
   */
  current_state?: DV_CODED_TEXT;
  /**
   * The ISM transition which occurred to arrive in the current_state. Coded by openEHR terminology group  Instruction transitions.
   */
  transition?: DV_CODED_TEXT;
  /**
   * The step in the careflow process which occurred as part of generating this action, e.g.  dispense ,  start_administration. This attribute represents the clinical  label for the activity, as  opposed to current_state which represents  the state machine (ISM)  computable form. Defined in archetype.
   */
  careflow_step?: DV_CODED_TEXT;
  /**
   * Optional possibility of adding one or more reasons for this careflow step having been taken. Multiple reasons may occur in medication management for example.
   */
  reason?: undefined;
}

/**
 * Used to record details of the Instruction causing an Action.
 */
export class INSTRUCTION_DETAILS extends PATHABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Reference to causing Instruction.
   */
  instruction_id?: openehr_base.LOCATABLE_REF;
  /**
   * Internal storage for activity_id
   * @protected
   */
  protected _activity_id?: openehr_base.String;

  /**
   * Identifier of Activity within Instruction, in the form of its archetype path.
   */
  get activity_id(): string | undefined {
    return this._activity_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for activity_id.
   * Use this to access openehr_base.String methods.
   */
  get $activity_id(): openehr_base.String | undefined {
    return this._activity_id;
  }

  /**
   * Sets activity_id from either a primitive value or openehr_base.String wrapper.
   */
  set activity_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._activity_id = undefined;
    } else if (typeof val === "string") {
      this._activity_id = openehr_base.String.from(val);
    } else {
      this._activity_id = val;
    }
  }

  /**
   * Various workflow engine state details, potentially including such things as:
   *
   * * condition that fired to cause this Action to be done (with actual variables substituted);
   * * list of notifications which actually occurred (with all variables substituted);
   * * other workflow engine state.
   *
   * This specification does not currently define the actual structure or semantics of this field.
   */
  wf_details?: ITEM_STRUCTURE;
}

/**
 * Used to specify actions in the future. Enables simple and complex specifications to be expressed, including in a fully-computable workflow form. Used for any actionable statement such as medication and therapeutic orders, monitoring, recall and review. Enough details must be provided for the specification to be directly executed by an actor, either human or machine.
 *
 * Not to be used for plan items which are only specified in general terms.
 */
export class INSTRUCTION extends CARE_ENTRY {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Mandatory human-readable version of what the Instruction is about.
   */
  narrative?: DV_TEXT;
  /**
   * Optional expiry date/time to assist determination of when an Instruction can be assumed to have expired. This helps prevent false listing of Instructions as Active when they clearly must have been terminated in some way or other.
   */
  expiry_time?: DV_DATE_TIME;
  /**
   * Optional workflow engine executable expression of the Instruction.
   */
  wf_definition?: DV_PARSABLE;
  /**
   * List of all activities in Instruction.
   */
  activities?: undefined;
}
